// ===============================================================
// NativeShellController.js — mounted once under <Router>, owns every
// piece of state the Android native bottom-navigation shell needs.
// ===============================================================
//
// Gated entirely behind REACT_APP_NATIVE_BOTTOM_NAV_SHELL === "true"
// (Android-only build-time feature flag). When the flag is false, or the
// native plugin genuinely isn't present/fails, this component does
// nothing beyond rendering null - src/components/BottomNav.js keeps
// rendering exactly as it always has on every page that already renders
// it, and no shell-state write or unread listener is created here at
// all ("no shell listener/read writes except unavoidable plugin
// registration" - plugin registration itself is native-side, in
// MainActivity, unaffected by this flag).
//
// Mounted as a plain always-on sibling in App.js (same tier as
// PresenceGate/CallListener/NativeHandoffListener - it renders null, it
// does not wrap the route tree), so the one boolean BottomNav.js needs
// (useIsNativeBottomNavShellActive) is published through a tiny module-
// level external store instead of React Context - a Context.Provider
// here could not reach BottomNav.js anyway, since BottomNav.js is a
// descendant of the SIBLING <AppRoutes/> element, not of this component.
//
// Owns, single-instance, for the whole app session:
//   - route policy (useLocation() + nativeBottomNavRoutePolicy)
//   - the ONE unread-count Firestore listener (moved out of
//     BottomNav.js, which now reads it via useIsNativeBottomNavShellActive
//     below purely to decide whether to render itself at all - BottomNav.js
//     keeps its OWN unread listener when the native shell is not active,
//     unchanged, so web/browser behavior is never affected)
//   - the monotonically increasing routeRevision sent with every
//     setShellState call, so an out-of-order async resolution can never
//     overwrite a newer route's state (native rejects any call whose
//     routeRevision is lower than the last one it already applied)
//   - the native shellReady/shellTabTapped handshake
//   - the shared "is native handling bottom-nav globally right now" flag

import { useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { useAccountSessionState, ACCOUNT_ACCESS_ALLOWED } from "../context/AccountSessionStateContext";
import { countSupportedUnread } from "../notifications/notificationTypes";
import { resolveRoutePolicy, TAB_CAMERA, TAB_NOTIFICATIONS } from "../navigation/nativeBottomNavRoutePolicy";
import {
  isNativeBottomNavAvailable,
  beginShellSession,
  endShellSession,
  setShellState,
  clearShellState,
  consumePendingShellCommand,
  addShellReadyListener,
  addShellTabTappedListener,
  consumePendingNotificationRoute,
  addNotificationRouteChangedListener,
} from "../plugins/NativeBottomNav";
import {
  issueShellSessionGeneration,
  advanceShellSessionGenerationPast,
} from "../plugins/shellSessionGeneration";

/**
 * Opaque, non-sensitive shell-session identifier - never derived from
 * uid/email/token/user data (see beginShellSession's own doc comment for
 * why a session boundary exists at all). crypto.randomUUID() is
 * preferred when available; the fallback is still unguessable-enough for
 * a same-process ownership token, never used for anything security-
 * sensitive.
 */
function createShellSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `shell-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// --- module-level external store (see comment above for why) ---
let shellActiveState = false;
const shellActiveListeners = new Set();

function publishShellActive(value) {
  if (shellActiveState !== value) {
    shellActiveState = value;
    shellActiveListeners.forEach((listener) => listener());
  }
}

function subscribeShellActive(callback) {
  shellActiveListeners.add(callback);
  return () => shellActiveListeners.delete(callback);
}

function getShellActiveSnapshot() {
  return shellActiveState;
}

/** BottomNav.js calls this to decide whether to suppress itself. */
export function useIsNativeBottomNavShellActive() {
  return useSyncExternalStore(subscribeShellActive, getShellActiveSnapshot, () => false);
}

// -----------------------------------------------------------------
// PHASE 11C-1 - the established shellSessionId/sessionGeneration/
// routeRevision identity, published here (never a second, parallel
// session concept) so NativeChatRouteBoundary.js can request native chat
// ownership using the EXACT SAME session native's own NativeBottomNavPlugin
// already tracks (see NativeChatOwnershipDecision.java's own doc comment
// for why cross-plugin session validation depends on this). Published
// only AFTER the value is actually established/advanced below - never a
// value this controller has not itself already committed to sending (or
// already sent) to native.
// -----------------------------------------------------------------
let chatHandoffIdentityState = { shellSessionId: null, sessionGeneration: 0, routeRevision: 0 };
const chatHandoffIdentityListeners = new Set();

function publishChatHandoffIdentity(next) {
  chatHandoffIdentityState = next;
  chatHandoffIdentityListeners.forEach((listener) => listener());
}

function subscribeChatHandoffIdentity(callback) {
  chatHandoffIdentityListeners.add(callback);
  return () => chatHandoffIdentityListeners.delete(callback);
}

function getChatHandoffIdentitySnapshot() {
  return chatHandoffIdentityState;
}

const DEFAULT_CHAT_HANDOFF_IDENTITY = { shellSessionId: null, sessionGeneration: 0, routeRevision: 0 };

/**
 * NativeChatRouteBoundary.js reads the current shellSessionId/
 * sessionGeneration/routeRevision through this hook - never re-derives or
 * re-issues its own. {@code shellSessionId} is null until this
 * controller's own session-establishing effect has run at least once
 * (mount) - a caller must treat a null shellSessionId as "not yet ready,
 * do not request ownership yet" (see that boundary's own doc comment).
 */
export function useNativeChatHandoffIdentity() {
  return useSyncExternalStore(
    subscribeChatHandoffIdentity, getChatHandoffIdentitySnapshot, () => DEFAULT_CHAT_HANDOFF_IDENTITY);
}

let unreadCountState = 0;
const unreadCountListeners = new Set();

function publishUnreadCount(value) {
  if (unreadCountState !== value) {
    unreadCountState = value;
    unreadCountListeners.forEach((listener) => listener());
  }
}

function subscribeUnreadCount(callback) {
  unreadCountListeners.add(callback);
  return () => unreadCountListeners.delete(callback);
}

function getUnreadCountSnapshot() {
  return unreadCountState;
}

/**
 * BottomNav.js reads the badge count through this hook instead of its
 * own onSnapshot listener whenever isNativeShellUnreadOwnerActive() is
 * true - see that function's own comment for exactly when ownership
 * transfers, and why "the feature flag is on" (not "the native bar has
 * fully taken over yet") is the correct switch point to avoid a brief
 * dual-listener window.
 */
export function useNativeShellUnreadCount() {
  return useSyncExternalStore(subscribeUnreadCount, getUnreadCountSnapshot, () => 0);
}

const FEATURE_ENABLED = process.env.REACT_APP_NATIVE_BOTTOM_NAV_SHELL === "true";

/**
 * True as soon as the native shell system is even POTENTIALLY going to
 * take over on this platform+build (flag on + plugin present) - NOT
 * gated on the handshake having fully completed yet. NativeShellController's
 * own unread-count effect below is unconditional on this same combination
 * (it never waits for shellActive before subscribing), so BottomNav.js
 * must stop running its OWN separate listener at exactly this same
 * point, not later - otherwise both listeners run simultaneously during
 * the brief pre-handshake window. On web/browser/iOS, or with the flag
 * off, this is always false and BottomNav.js's own listener remains the
 * sole owner, unchanged.
 */
export function isNativeShellUnreadOwnerActive() {
  return FEATURE_ENABLED && isNativeBottomNavAvailable();
}

export default function NativeShellController() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isReady } = useContext(AuthContext);
  const { access, genderTheme } = useAccountSessionState();

  const [unreadCount, setUnreadCountLocal] = useState(0);
  const routeRevisionRef = useRef(0);
  // CORRECTION (native-chat session churn / stale-event rejection):
  // routeRevisionRef above is the BOTTOM-NAV BAR's own out-of-order
  // fencing counter for setShellState - it must keep advancing on every
  // sendCurrentState() call (including one triggered solely by unreadCount
  // or another unrelated shell-state field), matching this file's own
  // long-established "monotonically increasing routeRevision sent with
  // every setShellState call" contract (see class doc comment). Native
  // chat ownership (NativeChatRouteBoundary.js) needs a DIFFERENT revision
  // with a DIFFERENT invariant: it must advance ONLY when the native-chat
  // route identity itself changes (the canonical chat route/pathname,
  // shellSessionId, or sessionGeneration) - never on a bare shell-state
  // publication. Conflating the two previously caused two opposite
  // failures depending on which was "fixed": reusing the bar's own
  // routeRevision made an unrelated unread-count update release+
  // reacquire the native-chat lease (a full ChatRepository.stop()/start()
  // churn cycle); naively dropping routeRevision from
  // NativeChatRouteBoundary's own dependency array left native's
  // ALREADY-ACCEPTED lease context pinned to a stale revision while this
  // bar-fencing counter kept climbing in the background, so any later
  // native-origin navigation event (e.g. header back) carried a revision
  // that could no longer match NativeChatRouteBoundary's own live value
  // and was silently rejected as stale. chatHandoffRevisionRef is the
  // separate, narrowly-scoped counter that resolves both: it only
  // advances when lastChatHandoffRouteRef detects the canonical route
  // itself changed (see sendCurrentState below), so it never drifts
  // during ordinary shell-state churn, and NativeChatRouteBoundary.js can
  // safely keep depending on it (restored dependency) without
  // reintroducing the churn this correction fixes.
  const chatHandoffRevisionRef = useRef(0);
  const lastChatHandoffRouteRef = useRef(null);
  const shellSessionIdRef = useRef(null);
  const sessionGenerationRef = useRef(0);
  const establishedSessionIdRef = useRef(null);
  // True only across a genuine unmount - see the mount/unmount-only
  // effect below. A uid change (without unmount) is detected separately,
  // by comparing shellSessionIdRef.current against the id an in-flight
  // call was issued for (see sendCurrentState's isStillCurrent()) - both
  // checks together are what let an obsolete async continuation detect
  // it has been superseded, however that happened.
  const cancelledRef = useRef(false);

  const uid = user?.uid || null;
  const authenticated = !!uid;
  const accountAccessAllowed = access === ACCOUNT_ACCESS_ALLOWED;

  // -----------------------------------------------------------------
  // Mount/unmount only (empty deps - never re-runs on a uid change, only
  // on true unmount) - the sole source of truth for "has this whole
  // controller instance been torn down", checked by sendCurrentState's
  // isStillCurrent() so an async continuation from an unmounted instance
  // can never call beginShellSession/setShellState or publish state.
  // -----------------------------------------------------------------
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // -----------------------------------------------------------------
  // A fresh shell session begins on mount AND on every uid change (login,
  // logout, account switch) - see beginShellSession's own doc comment for
  // why native needs this explicit boundary. routeRevisionRef is reset in
  // lockstep: revision counting always restarts at a fresh baseline for
  // a new session, never continuing from whatever the PRIOR
  // uid/session had reached (see sendCurrentState below, which pairs
  // this new id with beginShellSession before ever calling
  // setShellState). sessionGenerationRef is a strictly-increasing
  // fencing value (see issueShellSessionGeneration - never raw
  // Date.now(), which two sessions issued within the same millisecond,
  // e.g. a React StrictMode mount/cleanup/remount, could otherwise
  // collide on) that only beginShellSession's own accept decision uses -
  // see its own doc comment for why session identity alone cannot
  // protect that specific call. Declared before
  // sendCurrentState's own effect so it always commits first within the
  // same render.
  //
  // The cleanup below runs on BOTH a uid change and a true unmount (React
  // always cleans up the previous effect before the next runs, or on
  // unmount) - if this session was actually established with native, it
  // is proactively ended, so a delayed in-flight call issued from it can
  // never resurrect the bar later even if it does eventually reach
  // native (see endShellSession's own doc for why that alone is
  // conditional/safe even without this proactive call - this is
  // defense-in-depth, not the only protection).
  // -----------------------------------------------------------------
  useEffect(() => {
    shellSessionIdRef.current = createShellSessionId();
    sessionGenerationRef.current = issueShellSessionGeneration();
    establishedSessionIdRef.current = null;
    routeRevisionRef.current = 0;
    // A fresh session always restarts the chat-handoff revision at a
    // fresh baseline too - never continuing from whatever the PRIOR
    // uid/session had reached (mirrors routeRevisionRef's own reset
    // immediately above). lastChatHandoffRouteRef is seeded with the
    // CURRENT pathname so sendCurrentState's very next call (which this
    // same new session immediately triggers) does not spuriously bump
    // again for a route that hasn't actually changed since this reset.
    chatHandoffRevisionRef.current = 0;
    lastChatHandoffRouteRef.current = location.pathname;
    publishChatHandoffIdentity({
      shellSessionId: shellSessionIdRef.current,
      sessionGeneration: sessionGenerationRef.current,
      routeRevision: chatHandoffRevisionRef.current,
    });

    return () => {
      const idToEnd = establishedSessionIdRef.current;
      if (idToEnd) {
        void endShellSession(idToEnd);
      }
    };
    // location.pathname is deliberately read only as this fresh session's
    // starting baseline (see lastChatHandoffRouteRef comment above) -
    // never a reason for this effect itself to re-run; it stays keyed on
    // uid alone, exactly as before this correction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // -----------------------------------------------------------------
  // ONE unread-count listener for the whole app session - only while
  // signed in AND allowed, and only while the feature is enabled at all;
  // cleared immediately on any account change/logout.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!FEATURE_ENABLED) {
      return undefined;
    }
    setUnreadCountLocal(0);
    publishUnreadCount(0);

    if (!uid || !accountAccessAllowed) {
      return undefined;
    }

    const q = query(
      collection(db, "notifications"),
      where("uid", "==", uid),
      where("read", "==", false)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const count = countSupportedUnread(snap.docs);
        setUnreadCountLocal(count);
        publishUnreadCount(count);
      },
      (err) => {
        console.error("NativeShellController unread listener error:", err);
        setUnreadCountLocal(0);
        publishUnreadCount(0);
      }
    );

    return () => unsub();
  }, [uid, accountAccessAllowed]);

  // -----------------------------------------------------------------
  // Compute + send shell state whenever anything relevant changes.
  //
  // Activation is gated SOLELY on setShellState's own resolved
  // acknowledgement (applied:true for the exact routeRevision this call
  // sent) - never on having observed the "shellReady" event, which is a
  // fire-and-forget nudge Capacitor does not guarantee delivery of to a
  // listener that attaches after it fires (a real risk on cold start,
  // since MainShellCoordinator is constructed and started before the
  // WebView has necessarily finished loading index.html, let alone
  // before React has mounted this component). This is what makes
  // activation correct regardless of event-listener-attach timing.
  // -----------------------------------------------------------------
  const sendCurrentState = useCallback(async () => {
    if (!FEATURE_ENABLED || !isNativeBottomNavAvailable() || !isReady) {
      return;
    }

    const sessionId = shellSessionIdRef.current;
    let sessionGeneration = sessionGenerationRef.current;

    // True only while THIS call's session is still the live one - checked
    // before issuing each native call and again after each await
    // resolves, so an obsolete continuation (this controller unmounted,
    // or a newer uid/session already took over while we were awaiting)
    // can never issue a further call or publish state - see the
    // mount/unmount effect and the uid-keyed session effect above.
    const isStillCurrent = () => !cancelledRef.current && shellSessionIdRef.current === sessionId;

    if (establishedSessionIdRef.current !== sessionId) {
      // Must complete beginShellSession before ever sending/activating
      // state for this session (see beginShellSession's own doc).
      let result = await beginShellSession(sessionId, sessionGeneration);
      if (!isStillCurrent()) {
        return;
      }

      if (result && result.accepted !== true) {
        // Rejected. If this response carries usable feedback (see
        // NativeBottomNav.js's beginShellSession - it only ever returns
        // non-null with a valid shellSessionId/sessionGeneration), this is
        // very likely a cross-WebView/new-JS-context recovery scenario
        // (see shellSessionGeneration.js's own doc): THIS context's local
        // generator may have started below whatever generation native
        // still retains from a PRIOR context sharing the same Android
        // process (or, far less commonly, an equal-generation/different-
        // session-id collision). Rebase the local generator strictly past
        // native's reported current generation and retry EXACTLY ONCE
        // with a freshly issued generation and the SAME session id -
        // never for an already-superseded controller, so the lifecycle
        // guard is checked again immediately before rebasing and again
        // before retrying (requirement: never let a rejected OLD
        // controller use the returned native generation to interfere with
        // whatever controller superseded it).
        if (!isStillCurrent()) {
          return;
        }
        advanceShellSessionGenerationPast(result.sessionGeneration);

        if (!isStillCurrent()) {
          return;
        }
        const retryGeneration = issueShellSessionGeneration();
        sessionGenerationRef.current = retryGeneration;
        sessionGeneration = retryGeneration;
        // sessionGeneration itself is the identity change here - the
        // chat-handoff revision is republished unchanged (mirrors
        // routeRevisionRef's own pre-correction behavior at this exact
        // call site, which likewise never bumped here).
        publishChatHandoffIdentity({
          shellSessionId: shellSessionIdRef.current,
          sessionGeneration: retryGeneration,
          routeRevision: chatHandoffRevisionRef.current,
        });

        result = await beginShellSession(sessionId, retryGeneration);
        if (!isStillCurrent()) {
          return;
        }
      }

      if (!result || result.accepted !== true) {
        // Either the first attempt failed with no usable feedback to
        // recover from, or the single bounded retry also failed - remain
        // on the web fallback, never loop further. The next relevant
        // state change re-triggers this same effect and tries again from
        // scratch (a fresh issueShellSessionGeneration() call).
        return;
      }

      establishedSessionIdRef.current = sessionId;
    }

    if (!isStillCurrent()) {
      return;
    }

    const policy = resolveRoutePolicy(location.pathname);
    routeRevisionRef.current += 1;
    const thisRevision = routeRevisionRef.current;
    // The chat-handoff revision advances ONLY when the canonical route
    // itself changed since the last publish - never merely because this
    // sendCurrentState() call happened to run (e.g. an unreadCount-only
    // update) - see chatHandoffRevisionRef's own doc comment above for the
    // full "two incompatible purposes" rationale this resolves.
    // shellSessionId/sessionGeneration changes are already published
    // through their own dedicated call sites above, independent of this
    // one, so route is the only signal this call site needs to check.
    if (lastChatHandoffRouteRef.current !== location.pathname) {
      lastChatHandoffRouteRef.current = location.pathname;
      chatHandoffRevisionRef.current += 1;
    }
    publishChatHandoffIdentity({
      shellSessionId: sessionId,
      sessionGeneration,
      routeRevision: chatHandoffRevisionRef.current,
    });

    const accountAccessValue = accountAccessAllowed
      ? "allowed"
      : access === "unknown"
        ? "unknown"
        : "blocked";

    // Document-level "which route wants the bar hidden" attribute drives
    // --effective-bottom-nav-space's 10px/0px split (see theme.css) -
    // set synchronously, independent of the async acknowledgement below,
    // since it only matters once data-native-shell-active is already
    // "true" (CSS cascade), which itself only ever gets set from a
    // genuine applied:true acknowledgement further down.
    document.documentElement.setAttribute(
      "data-native-shell-bar-hidden",
      policy.barVisible && accountAccessAllowed && authenticated ? "false" : "true"
    );

    const result = await setShellState({
      shellSessionId: sessionId,
      activeTab: policy.activeTab,
      barVisible: policy.barVisible,
      authenticated,
      accountAccess: accountAccessValue,
      theme: genderTheme,
      unreadCount,
      fullScreenSuppressed: policy.fullScreenSuppressed,
      routeRevision: thisRevision,
    });

    if (!isStillCurrent()) {
      // Superseded (unmounted, or a newer uid/session already took over)
      // while setShellState was in flight - an obsolete continuation must
      // never publish shellActive or touch the DOM attribute, regardless
      // of what native's response says.
      return;
    }

    // A response for an older session, or an older revision than the
    // latest one THIS controller has since issued, must never activate
    // or overwrite whatever the newer, still-in-flight (or
    // already-resolved) request determines - silently ignored, touching
    // neither shellActive nor the DOM attribute. Compared against the
    // REFS' current (live) values rather than the locally-captured
    // sessionId/thisRevision, so a session that has since been replaced
    // (e.g. a fast logout+login while this call was in flight) is always
    // caught even in the (unlikely) event its numeric routeRevision
    // happens to coincide with the new session's own count.
    if (
      result.shellSessionId !== shellSessionIdRef.current ||
      result.routeRevision !== routeRevisionRef.current
    ) {
      return;
    }

    const shouldBeActive = result.applied === true && authenticated && accountAccessAllowed;
    publishShellActive(shouldBeActive);
    document.documentElement.setAttribute(
      "data-native-shell-active",
      shouldBeActive ? "true" : "false"
    );
  }, [location.pathname, isReady, authenticated, accountAccessAllowed, access, genderTheme, unreadCount]);

  useEffect(() => {
    void sendCurrentState();
  }, [sendCurrentState]);

  // -----------------------------------------------------------------
  // Native handshake: shellReady -> resend current state unconditionally.
  // shellTabTapped -> pull the pending command, navigate for a web-route
  // tab only (camera/notifications are already fully handled natively by
  // MainShellNavExecutor before any command reaches this queue).
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!FEATURE_ENABLED || !isNativeBottomNavAvailable()) {
      return undefined;
    }

    // Best-effort resend trigger only - see setShellState's own doc
    // comment. If this event is missed entirely (attached too late,
    // never fires, etc.), correctness is unaffected: the very first
    // effect run below already calls sendCurrentState() once, and its
    // own applied:true/false acknowledgement is what actually decides
    // activation.
    const readyHandle = addShellReadyListener(() => {
      void sendCurrentState();
    });

    const checkPendingTabCommand = async () => {
      const tab = await consumePendingShellCommand();
      if (!tab || tab === TAB_CAMERA || tab === TAB_NOTIFICATIONS) {
        return;
      }
      const routeByTab = {
        home: "/home",
        messages: "/messages",
        live: "/match-radar",
        profile: "/my-profile",
      };
      const path = routeByTab[tab];
      if (path) {
        navigate(path);
      }
    };

    const tappedHandle = addShellTabTappedListener(() => {
      void checkPendingTabCommand();
    });

    // A resolved in-list notification route (from MainActivity's own
    // native Notification Center content mode - see
    // NativeNotificationCenterController.onItemClicked). Pull-and-clear,
    // same shape as checkPendingTabCommand above - consuming it is what
    // prevents a stale route from ever being replayed.
    const checkPendingNotificationRoute = async () => {
      const path = await consumePendingNotificationRoute();
      if (path) {
        navigate(path);
      }
    };

    const notificationRouteHandle = addNotificationRouteChangedListener(() => {
      void checkPendingNotificationRoute();
    });

    return () => {
      void readyHandle?.remove?.();
      void tappedHandle?.remove?.();
      void notificationRouteHandle?.remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // -----------------------------------------------------------------
  // Logout/account-switch: clear native state immediately, before any
  // post-logout navigation completes.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!FEATURE_ENABLED || !isNativeBottomNavAvailable()) {
      return;
    }
    if (isReady && !uid) {
      void clearShellState();
      publishShellActive(false);
      document.documentElement.setAttribute("data-native-shell-active", "false");
    }
  }, [isReady, uid]);

  return null;
}
